import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw, Save, X, Copy, QrCode } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";

// Fallback copy function — works on HTTP too
function copyToClipboard(text) {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  ta.style.top = "-9999px";
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try {
    document.execCommand("copy");
    return true;
  } catch (e) {
    return false;
  } finally {
    document.body.removeChild(ta);
  }
}

function StatusDot({ status }) {
  const map = {
    online: "bg-green-500",
    offline: "bg-zinc-400",
    unpaired: "bg-amber-500",
  };
  return <span className={`inline-block h-2 w-2 rounded-full ${map[status] || "bg-zinc-400"}`} />;
}

function Editor({ initial, layouts, schedules, onCancel, onSave }) {
  const empty = { name: "Lobby TV", layout_id: layouts[0]?.id || "", schedule_id: schedules[0]?.id || "" };
  const [d, setD] = useState(initial ? { ...empty, ...initial } : empty);
  return (
    <div className="signage-card p-6 mb-6">
      <div className="flex items-center justify-between mb-5">
        <Input
          data-testid="device-name-input"
          value={d.name}
          onChange={(e) => setD({ ...d, name: e.target.value })}
          className="text-xl font-display font-bold rounded-none max-w-md h-10"
        />
        <div className="flex gap-2">
          <button onClick={onCancel} data-testid="device-cancel" className="signage-btn-secondary flex items-center gap-1"><X className="h-4 w-4" />Cancel</button>
          <button onClick={() => onSave(d)} data-testid="device-save" className="signage-btn-primary flex items-center gap-1"><Save className="h-4 w-4" />Save</button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="label-eyebrow">Layout</Label>
          <Select value={d.layout_id} onValueChange={(v) => setD({ ...d, layout_id: v })}>
            <SelectTrigger className="rounded-none mt-2 h-10" data-testid="device-layout-select"><SelectValue placeholder="Pick layout" /></SelectTrigger>
            <SelectContent>
              {layouts.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-eyebrow">Schedule</Label>
          <Select value={d.schedule_id} onValueChange={(v) => setD({ ...d, schedule_id: v })}>
            <SelectTrigger className="rounded-none mt-2 h-10" data-testid="device-schedule-select"><SelectValue placeholder="Pick schedule" /></SelectTrigger>
            <SelectContent>
              {schedules.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export default function Devices() {
  const [items, setItems] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [editing, setEditing] = useState(null);
  const [qrDevice, setQrDevice] = useState(null);

  const pairOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const pairUrl = (code) => `${pairOrigin}/pair?code=${code}`;

  const load = async () => {
    try {
      const [d, l, s] = await Promise.all([api.get("/devices"), api.get("/layouts"), api.get("/schedules")]);
      setItems(d.data); setLayouts(l.data); setSchedules(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const save = async (draft) => {
    try {
      if (draft.id) await api.put(`/devices/${draft.id}`, { name: draft.name, layout_id: draft.layout_id || null, schedule_id: draft.schedule_id || null });
      else await api.post("/devices", { name: draft.name, layout_id: draft.layout_id || null, schedule_id: draft.schedule_id || null });
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/devices/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const refresh = async (id) => {
    try { await api.post(`/devices/${id}/force-refresh`); toast.success("Refresh signal sent"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const refreshAll = async () => {
    try { await api.post("/devices/refresh-all"); toast.success("All devices will refresh"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const copyPair = (code) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(code).then(() => {
        toast.success(`Pairing code ${code} copied`);
      }).catch(() => {
        copyToClipboard(code) && toast.success(`Pairing code ${code} copied`);
      });
    } else {
      copyToClipboard(code) && toast.success(`Pairing code ${code} copied`);
    }
  };

  return (
    <div className="p-6 sm:p-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow">Devices</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-none mt-2">Screens online.</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={refreshAll} data-testid="refresh-all-button" className="signage-btn-secondary flex items-center gap-2"><RefreshCw className="h-4 w-4" />Refresh all</button>
          {!editing && (
            <button data-testid="new-device-button" onClick={() => setEditing("new")} className="signage-btn-primary flex items-center gap-2"><Plus className="h-4 w-4" />New device</button>
          )}
        </div>
      </div>

      {editing && (
        <Editor
          initial={editing === "new" ? null : editing}
          layouts={layouts}
          schedules={schedules}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}

      <div className="signage-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="px-4 py-3 label-eyebrow">Status</th>
              <th className="px-4 py-3 label-eyebrow">Name</th>
              <th className="px-4 py-3 label-eyebrow">Pairing code</th>
              <th className="px-4 py-3 label-eyebrow">Layout</th>
              <th className="px-4 py-3 label-eyebrow">Schedule</th>
              <th className="px-4 py-3 label-eyebrow">Last seen</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => {
              const lay = layouts.find((l) => l.id === d.layout_id);
              const sch = schedules.find((s) => s.id === d.schedule_id);
              return (
                <tr key={d.id} className="border-t border-zinc-200" data-testid={`device-row-${d.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <StatusDot status={d.status} />
                      <span className="text-xs uppercase tracking-wider">{d.status}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium">{d.name}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => copyPair(d.pairing_code)} className="font-mono-disp text-xs flex items-center gap-1 hover:text-[#002FA7]" data-testid={`copy-pair-${d.id}`}>
                      {d.pairing_code} <Copy className="h-3 w-3" />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-zinc-600">{lay?.name || "—"}</td>
                  <td className="px-4 py-3 text-zinc-600">{sch?.name || "—"}</td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{d.last_seen ? new Date(d.last_seen).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button data-testid={`qr-device-${d.id}`} title="Tampilkan QR code untuk pairing" onClick={() => setQrDevice(d)} className="p-1.5 hover:bg-zinc-100"><QrCode className="h-4 w-4" /></button>
                      <button data-testid={`refresh-device-${d.id}`} title="Force refresh" onClick={() => refresh(d.id)} className="p-1.5 hover:bg-zinc-100"><RefreshCw className="h-4 w-4" /></button>
                      <button data-testid={`edit-device-${d.id}`} onClick={() => setEditing(d)} className="text-xs px-2 hover:underline">Edit</button>
                      <button data-testid={`delete-device-${d.id}`} onClick={() => remove(d.id)} className="p-1.5 hover:bg-red-50"><Trash2 className="h-4 w-4 text-red-600" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-10 text-center text-zinc-500">No devices yet. Add a device to get a pairing code, then open <span className="font-mono-disp">/pair</span> on your TV.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-8 signage-card p-5">
        <p className="label-eyebrow mb-2">How to pair a TV</p>
        <ol className="text-sm space-y-1 text-zinc-600 list-decimal pl-5">
          <li>Klik <strong>New device</strong> di atas, simpan — lalu klik ikon <strong>QR</strong> di baris device baru.</li>
          <li>Scan QR pakai HP, lalu kirim link itu ke TV — atau langsung tampilkan QR di depan TV bila browser TV mendukung scan.</li>
          <li>Alternatif: di TV browser, ketik <span className="font-mono-disp">{pairOrigin}/pair</span> lalu masukkan kode 6-karakter.</li>
        </ol>
      </div>

      {/* QR dialog */}
      <Dialog open={!!qrDevice} onOpenChange={(o) => !o && setQrDevice(null)}>
        <DialogContent className="rounded-none max-w-md" data-testid="qr-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Pair {qrDevice?.name}</DialogTitle>
            <DialogDescription>
              Scan QR ini dari HP, atau ketik URL di bawah pada browser TV. Kode pairing terisi otomatis.
            </DialogDescription>
          </DialogHeader>
          {qrDevice && (
            <div className="space-y-4">
              <div className="flex justify-center py-2">
                <div className="p-4 bg-white border border-zinc-200">
                  <QRCodeSVG
                    value={pairUrl(qrDevice.pairing_code)}
                    size={240}
                    level="M"
                    includeMargin={false}
                    fgColor="#002FA7"
                  />
                </div>
              </div>
              <div>
                <Label className="label-eyebrow">URL pairing</Label>
                <div className="flex mt-2 gap-2">
                  <Input
                    readOnly
                    value={pairUrl(qrDevice.pairing_code)}
                    className="rounded-none font-mono-disp text-xs h-9"
                    onFocus={(e) => e.target.select()}
                    data-testid="qr-url-input"
                  />
                  <button
                    onClick={() => {
                      if (navigator.clipboard && window.isSecureContext) {
                        navigator.clipboard.writeText(pairUrl(qrDevice.pairing_code)).then(() => toast.success("URL disalin"));
                      } else {
                        copyToClipboard(pairUrl(qrDevice.pairing_code)) && toast.success("URL disalin");
                      }
                    }}
                    className="signage-btn-secondary text-xs px-3"
                    data-testid="qr-copy-url"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div>
                  <p className="label-eyebrow">Kode pairing</p>
                  <p className="font-mono-disp text-2xl font-bold mt-1">{qrDevice.pairing_code}</p>
                </div>
                <div>
                  <p className="label-eyebrow">Nama device</p>
                  <p className="text-sm font-medium mt-1">{qrDevice.name}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
